import React, { useEffect, useRef, useState } from "react";
import { Button } from "@mui/material";
import { GoogleMap, PolygonF } from "@react-google-maps/api";
import { TerraDraw, TerraDrawPolygonMode } from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";
import { useTranslation } from "react-i18next";
import { mapStyle } from "../../components/map/constants";
import { LatLng } from "../../types/matchRate.types";

interface MatchRatePolygonMapProps {
  polygon?: LatLng[];
  onChange: (polygon: LatLng[] | undefined) => void;
}

const lithuaniaCenter = { lat: 55.17, lng: 23.88 };

const polygonOptions = {
  strokeColor: "#4A3AFF",
  strokeWeight: 2,
  fillColor: "#4A3AFF",
  fillOpacity: 0.1,
};

export const MatchRatePolygonMap: React.FC<MatchRatePolygonMapProps> = ({
  polygon,
  onChange,
}) => {
  const { t } = useTranslation();
  const mapRef = useRef<google.maps.Map | null>(null);
  const terraDrawRef = useRef<TerraDraw | null>(null);
  const onChangeRef = useRef(onChange);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!isDrawing) {
      return;
    }

    const map = mapRef.current;
    if (!map) {
      return;
    }

    const initTerraDraw = () => {
      const draw = new TerraDraw({
        adapter: new TerraDrawGoogleMapsAdapter({
          lib: google.maps,
          map,
          coordinatePrecision: 9,
        }),
        modes: [
          new TerraDrawPolygonMode({
            styles: {
              fillColor: "#4A3AFF",
              fillOpacity: 0.1,
              outlineColor: "#4A3AFF",
              outlineWidth: 2,
              closingPointColor: "#4A3AFF",
              closingPointWidth: 4,
              closingPointOutlineColor: "#ffffff",
              closingPointOutlineWidth: 1,
            },
          }),
        ],
      });

      draw.start();
      draw.on("ready", () => {
        draw.setMode("polygon");
      });
      draw.on("finish", (id: string | number) => {
        const snapshot = draw.getSnapshot();
        const feature = snapshot.find((item) => item.id === id);
        if (feature?.geometry.type === "Polygon") {
          const ring = feature.geometry.coordinates[0] as [number, number][];
          const paths = ring.slice(0, -1).map(([lng, lat]) => ({ lat, lng }));
          if (paths.length >= 3) {
            onChangeRef.current(paths);
          }
          draw.stop();
          terraDrawRef.current = null;
          setIsDrawing(false);
        }
      });
      terraDrawRef.current = draw;
    };

    if (map.getProjection()) {
      initTerraDraw();
    } else {
      const listener = map.addListener("projection_changed", () => {
        google.maps.event.removeListener(listener);
        initTerraDraw();
      });
      return () => google.maps.event.removeListener(listener);
    }

    return () => {
      terraDrawRef.current?.stop();
      terraDrawRef.current = null;
    };
  }, [isDrawing]);

  return (
    <div>
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
        <Button
          variant="outlined"
          onClick={() => setIsDrawing(true)}
          disabled={isDrawing}
        >
          {t("matchRate.drawPolygon")}
        </Button>
        <Button
          variant="text"
          disabled={!polygon?.length && !isDrawing}
          onClick={() => {
            terraDrawRef.current?.stop();
            terraDrawRef.current = null;
            setIsDrawing(false);
            onChange(undefined);
          }}
        >
          {t("matchRate.clearPolygon")}
        </Button>
      </div>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "360px", borderRadius: 10 }}
        center={lithuaniaCenter}
        zoom={7}
        options={{
          styles: mapStyle,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        }}
        onLoad={(map) => {
          mapRef.current = map;
        }}
      >
        {!!polygon?.length && !isDrawing && (
          <PolygonF path={polygon} options={polygonOptions} />
        )}
      </GoogleMap>
    </div>
  );
};
